// The print pipeline's one entry point: a book in, the two press-ready PDFs a printer asks for out. It knows nothing
// about any printer; a vendor adapter takes these files (and, if it has one, gives the exact cover size first). A book
// is anything that can lay out its interior and its cover: a cookbook, or an album's Storybook version.
import type { Album } from '../albums'
import type { Cookbook } from '../cookbooks'
import type { CookbookLook, RecipeEdits } from '../cookbook/looks'
import { coverSize, type CoverSize, type PrintFormat } from '../../../shared/print'
import type { CoverPdf, Interior, Layout } from './kit'
import type { Look } from './looks'
import type { PhotoPageEdits } from '../storybook'

export type Printable = {
  title: string
  interior: (layout?: Layout) => Promise<Interior>
  cover: (format: PrintFormat, size: CoverSize) => Promise<CoverPdf>
}

export const printableCookbook = (book: Cookbook, look?: CookbookLook, edits?: RecipeEdits): Printable => ({
  title: book.title,
  interior: async (layout) => (await import('../cookbook/pdf')).cookbookPdf(book, layout, look, edits),
  cover: async (format, size) => (await import('../cookbook/pdf')).cookbookCoverPdf(book, format, size, look),
})

export const printableStorybook = (album: Album, look?: Look, edits?: PhotoPageEdits): Printable => ({
  title: album.title,
  interior: async (layout) => (await import('./storybook')).storybookPdf(album, layout, look, edits),
  cover: async (format, size) => (await import('./storybook')).storybookCoverPdf(album, format, size, look),
})

export type PrintFiles = {
  interior: Uint8Array
  cover: Uint8Array
  pages: number
  // Ruled notes pages added to reach the binding's fewest pages.
  added: number
  coverSize: CoverSize
  // The photos below print resolution where they're drawn.
  lowRes: string[]
}

export class TooManyPages extends Error {}

export async function buildPrintPDF(
  book: Printable,
  format: PrintFormat,
  options: { coverSize?: (pages: number) => Promise<CoverSize> } = {},
): Promise<PrintFiles> {
  const interior = await book.interior({ trim: format.trim, minPages: format.minPages, bound: true })
  if (interior.pages > format.maxPages)
    throw new TooManyPages(`This book has ${interior.pages} pages; a ${format.binding} can have ${format.maxPages}.`)
  const size = options.coverSize ? await options.coverSize(interior.pages) : coverSize(format, interior.pages)
  const cover = await book.cover(format, size)
  return {
    interior: interior.bytes,
    cover: cover.bytes,
    pages: interior.pages,
    added: interior.added,
    coverSize: size,
    lowRes: [...new Set([...cover.lowRes, ...interior.lowRes])],
  }
}
